import { useEffect, useState } from 'react';

import {
  Activity,
  ArrowLeft,
  BarChart3,
  ChevronDown,
  ChevronRight,
  ChevronUp,
  Info,
  KanbanSquare,
  ListPlus,
  PieChart,
  Plus,
  Search,
  Table2,
  TrendingUp,
  Type,
  X,
} from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Separator } from '@/components/ui/separator';
import { SimpleTooltip } from '@/components/ui/simple-tooltip';
import { Switch } from '@/components/ui/switch';
import { Textarea } from '@/components/ui/textarea';

import { DIMENSION_KEY_USER } from '../constants/report-dimension-keys';
import { useAvailableColumns } from '../hooks/use-available-columns';
import { useDeleteFormulaEntry } from '../hooks/use-delete-formula-entry';
import { useFormulaEntries } from '../hooks/use-formula-entries';
import { useUpdateElement } from '../hooks/use-update-element';
import {
  DateFilter,
  FormulaLibraryEntry,
  FormulaScope,
  Report,
  ReportElement,
  ReportElementType,
} from '../types';
import { ReportDimensionEntityDTO, ReportDimensionResponse } from '../types/report-dimension';
import { CHART_COLORS } from '../utils/format-measure';
import { DroppingItem } from './canvas-grid';
import { DimensionLabel } from './dimension-label';
import { DimensionPicker, DimensionPickerTrigger } from './dimension-picker';
import { FormulaDialog } from './formula-dialog';
import { ELEMENT_ICONS } from './report-element';

// ─── Panel navigation stack ───────────────────────────────────────────────────

type PanelFrame =
  | { kind: 'element'; elementId: string }
  | { kind: 'formula-library'; elementId: string };

// ─── Constants ────────────────────────────────────────────────────────────────

const WIDTH_PRESETS = [
  { label: '1/3', value: 4 },
  { label: '1/2', value: 6 },
  { label: '2/3', value: 8 },
  { label: 'Full', value: 12 },
];

const REPORT_TYPES = [
  { value: 'TIME_TRACKS', labelKey: 'dataset.TIME_TRACKS' },
  { value: 'OVERHEADS', labelKey: 'dataset.OVERHEADS' },
  { value: 'PROFITABILITY', labelKey: 'dataset.PROFITABILITY' },
  { value: 'TIME_OFFS', labelKey: 'dataset.TIME_OFFS' },
  { value: 'CAPACITY_PLANNING', labelKey: 'dataset.CAPACITY_PLANNING' },
  { value: 'ALLOCATION_PLANNING', labelKey: 'dataset.ALLOCATION_PLANNING' },
  { value: 'PIPELINE_PLANNING', labelKey: 'dataset.PIPELINE_PLANNING' },
  { value: 'TIME_OFF_PLANNING', labelKey: 'dataset.TIME_OFF_PLANNING' },
];

const MEASURE_TYPE_LABELS: Record<string, string> = {
  hours: 'Hours',
  money: 'Currency',
  percent: 'Percent',
  number: 'Number',
  ratio: 'Ratio',
};

const DRAGGABLE_ELEMENT_TYPES: {
  type: ReportElementType;
  icon: React.ElementType;
  defaultW: number;
  defaultH: number;
}[] = [
  { type: ReportElementType.TABLE, icon: Table2, defaultW: 8, defaultH: 5 },
  { type: ReportElementType.KPI_CARD, icon: KanbanSquare, defaultW: 3, defaultH: 2 },
  { type: ReportElementType.BAR_CHART, icon: BarChart3, defaultW: 6, defaultH: 5 },
  { type: ReportElementType.LINE_CHART, icon: TrendingUp, defaultW: 6, defaultH: 5 },
  { type: ReportElementType.AREA_CHART, icon: Activity, defaultW: 6, defaultH: 5 },
  { type: ReportElementType.PIE_CHART, icon: PieChart, defaultW: 4, defaultH: 5 },
  { type: ReportElementType.TEXT_BLOCK, icon: Type, defaultW: 6, defaultH: 2 },
];

// ─── Shared ───────────────────────────────────────────────────────────────────

interface PanelHeaderProps {
  backLabel: string;
  title: string;
  onBack: () => void;
}

const PanelHeader = ({ backLabel, title, onBack }: PanelHeaderProps) => (
  <div className="flex shrink-0 items-center gap-1 border-b px-2 py-2">
    <Button variant="ghost" size="icon" className="h-7 w-7 shrink-0" onClick={onBack}>
      <ArrowLeft className="h-4 w-4" />
    </Button>
    <span className="text-sm text-muted-foreground">{backLabel}</span>
    {/* eslint-disable-next-line i18next/no-literal-string */}
    <span className="text-sm text-muted-foreground">·</span>
    <span className="min-w-0 truncate text-sm font-medium">{title}</span>
  </div>
);

interface SectionLabelProps {
  children: React.ReactNode;
}

const SectionLabel = ({ children }: SectionLabelProps) => (
  <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">{children}</p>
);

// ─── Root panel ───────────────────────────────────────────────────────────────

interface ElementPaletteProps {
  onDragStart: (item: DroppingItem) => void;
  onDragEnd: () => void;
}

const ElementPalette = ({ onDragStart, onDragEnd }: ElementPaletteProps) => {
  const { t } = useTranslation('authenticated.reports');

  return (
    <div className="flex flex-wrap gap-1.5">
      {DRAGGABLE_ELEMENT_TYPES.map(({ type, icon: DragIcon, defaultW, defaultH }) => (
        <div
          key={type}
          role="button"
          tabIndex={0}
          draggable
          unselectable="on"
          title={t(`element.${type}`)}
          onDragStart={e => {
            e.dataTransfer.setData('text/plain', '');
            onDragStart({ type, w: defaultW, h: defaultH });
          }}
          onDragEnd={onDragEnd}
          onKeyDown={() => {}}
          className="flex h-9 w-9 cursor-grab items-center justify-center rounded-md border bg-background text-muted-foreground transition-colors hover:bg-muted hover:text-foreground active:cursor-grabbing"
        >
          <DragIcon className="h-4 w-4" />
        </div>
      ))}
    </div>
  );
};

interface RootPanelProps {
  report: Report;
  editedName: string;
  onNameChange: (name: string) => void;
  onDragStart: (item: DroppingItem) => void;
  onDragEnd: () => void;
  onNavigateToElement: (elementId: string) => void;
}

const RootPanel = ({
  report,
  editedName,
  onNameChange,
  onDragStart,
  onDragEnd,
  onNavigateToElement,
}: RootPanelProps) => {
  const { t } = useTranslation('authenticated.reports');

  return (
    <div className="flex h-full w-72 shrink-0 flex-col overflow-y-auto rounded-lg border bg-card">
      <div className="p-4">
        <SectionLabel>{t('panel.report')}</SectionLabel>
        <Input
          className="mt-2 h-8"
          value={editedName}
          onChange={e => onNameChange(e.target.value)}
          placeholder={report.name}
        />
      </div>

      <Separator />

      <div className="p-4">
        <SectionLabel>{t('panel.globalFilters')}</SectionLabel>
        <p className="mt-2 text-xs text-muted-foreground">{t('panel.globalFiltersNote')}</p>
      </div>

      <Separator />

      <div className="flex flex-col p-4">
        <SectionLabel>{t('panel.addElement')}</SectionLabel>
        <div className="mt-3">
          <ElementPalette onDragStart={onDragStart} onDragEnd={onDragEnd} />
        </div>
        <p className="mt-2 text-xs text-muted-foreground">{t('panel.dragHint')}</p>
      </div>

      {report.elements.length > 0 && (
        <>
          <Separator />
          <div className="flex flex-col p-4">
            <SectionLabel>{t('panel.elements')}</SectionLabel>
            <div className="mt-3 flex flex-col gap-1.5">
              {report.elements.map(el => {
                const Icon = ELEMENT_ICONS[el.type] ?? BarChart3;
                const displayName =
                  (el.config.title as string | undefined) || t(`element.${el.type}`);
                return (
                  <button
                    key={el.id}
                    type="button"
                    onClick={() => onNavigateToElement(el.id)}
                    className="flex w-full items-center gap-2 rounded-md border bg-background px-2.5 py-2 text-left transition-colors hover:bg-muted"
                  >
                    <Icon className="h-4 w-4 shrink-0 text-muted-foreground" />
                    <div className="min-w-0 flex-1">
                      <p className="truncate text-sm font-medium leading-none">{displayName}</p>
                      {el.reportType && (
                        <p className="mt-0.5 truncate text-xs text-muted-foreground">
                          {t(`dataset.${el.reportType}`)}
                          {el.columns.length > 0 &&
                            ` · ${t('dataset.cols', { count: el.columns.length })}`}
                        </p>
                      )}
                    </div>
                    <ChevronRight className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
                  </button>
                );
              })}
            </div>
          </div>
        </>
      )}
    </div>
  );
};

// ─── Element panel sub-sections ───────────────────────────────────────────────

interface WidthSelectorProps {
  currentWidth: number;
  onChange: (width: number) => void;
}

const WidthSelector = ({ currentWidth, onChange }: WidthSelectorProps) => {
  const { t } = useTranslation('authenticated.reports');
  return (
    <div className="flex flex-col gap-2 px-4 py-3">
      <SectionLabel>{t('element.width')}</SectionLabel>
      <div className="flex rounded-md border bg-muted p-0.5">
        {WIDTH_PRESETS.map(preset => (
          <button
            key={preset.value}
            type="button"
            onClick={() => onChange(preset.value)}
            className={`flex-1 rounded py-1 text-xs transition-colors ${
              currentWidth === preset.value
                ? 'bg-background font-medium shadow-sm'
                : 'text-muted-foreground hover:text-foreground'
            }`}
          >
            {preset.label}
          </button>
        ))}
      </div>
    </div>
  );
};

interface TableColumnsProps {
  element: ReportElement;
  reportId: string;
  measures: Array<{ key: string; label: string; type: string }>;
  formulaEntries: FormulaLibraryEntry[];
}

const TableColumns = ({ element, reportId, measures, formulaEntries }: TableColumnsProps) => {
  const { t } = useTranslation('authenticated.reports');
  const [columns, setColumns] = useState<string[]>(element.columns);
  const [search, setSearch] = useState('');
  const [showTotalsRow, setShowTotalsRow] = useState<boolean>(
    element.config?.showTotalsRow === true
  );
  const [showGroupSummaries, setShowGroupSummaries] = useState<boolean>(
    element.config?.showGroupSummaries === true
  );
  const { mutate: updateElement } = useUpdateElement();

  const regularItems = measures.map(m => ({
    key: m.key,
    label: m.label,
    type: m.type,
    isFormula: false as const,
    hint: undefined as string | undefined,
  }));
  const formulaItems = (formulaEntries ?? []).map(f => ({
    key: f.key,
    label: f.label,
    type: f.measureType,
    isFormula: true as const,
    hint: f.formulaString,
  }));
  const allItems = [...regularItems, ...formulaItems];

  const save = (next: string[]) => {
    setColumns(next);
    updateElement({ reportId, elementId: element.id, body: { columns: next } });
  };

  const moveColumn = (key: string, dir: -1 | 1) => {
    const idx = columns.indexOf(key);
    const target = idx + dir;
    if (target < 0 || target >= columns.length) return;
    const next = [...columns];
    [next[idx], next[target]] = [next[target], next[idx]];
    save(next);
  };

  if (allItems.length === 0) {
    return <p className="text-xs text-muted-foreground">{t('configurator.noColumns')}</p>;
  }

  const selectedItems = columns
    .map(key => allItems.find(i => i.key === key))
    .filter((i): i is (typeof allItems)[number] => i != null);
  const availableRegular = regularItems
    .filter(i => !columns.includes(i.key))
    .filter(i => !search.trim() || i.label.toLowerCase().includes(search.toLowerCase()));
  const availableFormulas = formulaItems.filter(i => !columns.includes(i.key));

  return (
    <>
      <SectionLabel>{t('configurator.columns')}</SectionLabel>

      {selectedItems.length > 0 ? (
        <div className="flex flex-col gap-1">
          {selectedItems.map((item, index) => (
            <div
              key={item.key}
              className="flex items-center gap-2 rounded-md border bg-background px-2.5 py-1.5 text-sm"
            >
              {item.isFormula && (
                // eslint-disable-next-line i18next/no-literal-string
                <span className="shrink-0 font-mono text-xs text-primary">ƒ</span>
              )}
              <span className="min-w-0 flex-1 truncate">{item.label}</span>
              {item.hint && (
                <SimpleTooltip
                  content={<span className="font-mono">{item.hint}</span>}
                  side="left"
                  variant="light"
                  contentClassName="max-w-xs text-xs"
                >
                  <span className="cursor-help text-muted-foreground">
                    <Info className="h-3 w-3 shrink-0" />
                  </span>
                </SimpleTooltip>
              )}
              <div className="flex shrink-0 gap-0.5">
                <button
                  disabled={index === 0}
                  onClick={() => moveColumn(item.key, -1)}
                  className="text-muted-foreground hover:text-foreground disabled:opacity-30"
                >
                  <ChevronUp className="h-3.5 w-3.5" />
                </button>
                <button
                  disabled={index === selectedItems.length - 1}
                  onClick={() => moveColumn(item.key, 1)}
                  className="text-muted-foreground hover:text-foreground disabled:opacity-30"
                >
                  <ChevronDown className="h-3.5 w-3.5" />
                </button>
                <button
                  onClick={() => save(columns.filter(k => k !== item.key))}
                  className="text-muted-foreground hover:text-foreground"
                >
                  <X className="h-3.5 w-3.5" />
                </button>
              </div>
            </div>
          ))}
        </div>
      ) : (
        <p className="text-xs text-muted-foreground">{t('configurator.noColumnsSelected')}</p>
      )}

      {regularItems.some(i => !columns.includes(i.key)) && (
        <div className="flex flex-col gap-1">
          <div className="relative">
            <Search className="pointer-events-none absolute left-2 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground" />
            <Input
              value={search}
              onChange={e => setSearch(e.target.value)}
              placeholder={t('configurator.searchColumns')}
              className="h-7 pl-7 text-xs"
            />
          </div>
          <div className="flex max-h-48 flex-col overflow-y-auto">
            {availableRegular.map(item => (
              <button
                key={item.key}
                type="button"
                onClick={() => save([...columns, item.key])}
                className="flex items-center gap-2 rounded px-1.5 py-1.5 text-left text-sm text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
              >
                <Plus className="h-3.5 w-3.5 shrink-0" />
                <span className="truncate">{item.label}</span>
              </button>
            ))}
            {availableRegular.length === 0 && search.trim() && (
              <p className="px-1.5 py-2 text-xs text-muted-foreground">
                {t('configurator.noSearchResults')}
              </p>
            )}
          </div>
        </div>
      )}

      {formulaItems.length > 0 && (
        <>
          <SectionLabel>{t('configurator.customColumns')}</SectionLabel>
          {availableFormulas.length > 0 ? (
            <div className="flex flex-col">
              {availableFormulas.map(item => (
                <div
                  key={item.key}
                  className="flex items-center rounded transition-colors hover:bg-muted"
                >
                  <button
                    type="button"
                    onClick={() => save([...columns, item.key])}
                    className="flex flex-1 items-center gap-2 px-1.5 py-1.5 text-left text-sm text-muted-foreground hover:text-foreground"
                  >
                    <Plus className="h-3.5 w-3.5 shrink-0" />
                    {/* eslint-disable-next-line i18next/no-literal-string */}
                    <span className="shrink-0 font-mono text-xs text-primary">ƒ</span>
                    <span className="truncate">{item.label}</span>
                  </button>
                  {item.hint && (
                    <SimpleTooltip
                      content={<span className="font-mono">{item.hint}</span>}
                      side="left"
                      variant="light"
                      contentClassName="max-w-xs text-xs"
                    >
                      <span className="cursor-help px-1.5 text-muted-foreground">
                        <Info className="h-3 w-3" />
                      </span>
                    </SimpleTooltip>
                  )}
                </div>
              ))}
            </div>
          ) : (
            <p className="text-xs text-muted-foreground">{t('configurator.allCustomAdded')}</p>
          )}
        </>
      )}

      <p className="text-xs text-muted-foreground">{t('configurator.columnsNote')}</p>

      <Separator />

      <div className="flex items-center justify-between gap-2">
        <span className="text-sm">{t('configurator.showTotalsRow')}</span>
        <Switch
          checked={showTotalsRow}
          onCheckedChange={checked => {
            setShowTotalsRow(checked);
            updateElement({
              reportId,
              elementId: element.id,
              body: { config: { ...element.config, showTotalsRow: checked } },
            });
          }}
        />
      </div>

      {element.groupBy.length >= 2 && (
        <div className="flex items-center justify-between gap-2">
          <span className="text-sm">{t('configurator.showGroupSummaries')}</span>
          <Switch
            checked={showGroupSummaries}
            onCheckedChange={checked => {
              setShowGroupSummaries(checked);
              updateElement({
                reportId,
                elementId: element.id,
                body: { config: { ...element.config, showGroupSummaries: checked } },
              });
            }}
          />
        </div>
      )}
    </>
  );
};

interface CartesianChartConfigProps {
  element: ReportElement;
  reportId: string;
  measures: Array<{ key: string; label: string; type: string }>;
  dimensions: ReportDimensionResponse[];
  formulaEntries: FormulaLibraryEntry[];
}

type ChartTarget = { label: string; value: number };
type ChartZone = { from?: number; to?: number; color: string; label?: string };

const CartesianChartConfig = ({
  element,
  reportId,
  measures,
  dimensions,
  formulaEntries,
}: CartesianChartConfigProps) => {
  const { t } = useTranslation('authenticated.reports');
  const [columns, setColumns] = useState<string[]>(element.columns);
  const [groupBy, setGroupBy] = useState<string[]>(element.groupBy);
  const [seriesBy, setSeriesBy] = useState<string | null>(element.pivotDimension);
  const [measureSearch, setMeasureSearch] = useState('');
  const [showAverageLine, setShowAverageLine] = useState<boolean>(
    element.config?.showAverageLine === true
  );
  const [averageFormula, setAverageFormula] = useState<string>(
    (element.config?.averageFormula as string | undefined) ?? ''
  );
  const [targets, setTargets] = useState<ChartTarget[]>(() => {
    const raw = element.config?.targets;
    return Array.isArray(raw) ? (raw as ChartTarget[]) : [];
  });
  const [newTargetLabel, setNewTargetLabel] = useState('');
  const [newTargetValue, setNewTargetValue] = useState('');
  const [zones, setZones] = useState<ChartZone[]>(() => {
    const raw = element.config?.zones;
    return Array.isArray(raw) ? (raw as ChartZone[]) : [];
  });
  const [newZoneFrom, setNewZoneFrom] = useState('');
  const [newZoneTo, setNewZoneTo] = useState('');
  const [newZoneColor, setNewZoneColor] = useState('#ef444420');
  const [newZoneLabel, setNewZoneLabel] = useState('');
  const { mutate: updateElement } = useUpdateElement();

  const saveConfig = (patch: Record<string, unknown>) => {
    updateElement({
      reportId,
      elementId: element.id,
      body: { config: { ...element.config, ...patch } },
    });
  };

  const save = (c: string[], g: string[], s: string | null = seriesBy) => {
    updateElement({
      reportId,
      elementId: element.id,
      body: { columns: c, groupBy: g, pivotDimension: s },
    });
  };

  const xAxisKey = groupBy[0] ?? null;
  const xAxisDim = xAxisKey ? dimensions.find(d => d.key === xAxisKey) : null;

  const setXAxis = (key: string | null) => {
    const next = key ? [key] : [];
    setGroupBy(next);
    save(columns, next);
  };

  const moveMeasure = (key: string, dir: -1 | 1) => {
    const idx = columns.indexOf(key);
    const target = idx + dir;
    if (target < 0 || target >= columns.length) return;
    const next = [...columns];
    [next[idx], next[target]] = [next[target], next[idx]];
    setColumns(next);
    save(next, groupBy);
  };

  const setSeriesDim = (key: string | null) => {
    setSeriesBy(key);
    save(columns, groupBy, key);
  };

  const activeSeriesDim = seriesBy ? dimensions.find(d => d.key === seriesBy) : null;

  const regularMeasures = measures.map(m => ({
    key: m.key,
    label: m.label,
    type: m.type,
    isFormula: false as const,
    hint: undefined as string | undefined,
  }));
  const formulaMeasures = (formulaEntries ?? []).map(f => ({
    key: f.key,
    label: f.label,
    type: f.measureType,
    isFormula: true as const,
    hint: f.formulaString,
  }));
  const allMeasures = [...regularMeasures, ...formulaMeasures];

  const selectedMeasures = columns
    .map(key => allMeasures.find(m => m.key === key))
    .filter((m): m is (typeof allMeasures)[number] => m != null);
  const filteredAvailableRegularMeasures = regularMeasures
    .filter(m => !columns.includes(m.key))
    .filter(
      m => !measureSearch.trim() || m.label.toLowerCase().includes(measureSearch.toLowerCase())
    );
  const availableFormulaMeasures = formulaMeasures.filter(m => !columns.includes(m.key));

  return (
    <>
      <SectionLabel>{t('configurator.xAxis')}</SectionLabel>
      {xAxisDim ? (
        <div className="flex items-center gap-2 rounded-md border bg-background px-2.5 py-1.5 text-sm">
          <span className="flex-1 truncate">
            <DimensionLabel dimension={xAxisDim} />
          </span>
          <button
            onClick={() => setXAxis(null)}
            className="text-muted-foreground hover:text-foreground"
          >
            <X className="h-3.5 w-3.5" />
          </button>
        </div>
      ) : (
        <>
          <p className="text-xs text-muted-foreground">{t('configurator.xAxisHint')}</p>
          <DimensionPicker
            dimensions={dimensions}
            usedKeys={[xAxisKey, seriesBy]}
            onSelect={setXAxis}
          >
            <DimensionPickerTrigger>{t('dimensionPicker.chooseXAxis')}</DimensionPickerTrigger>
          </DimensionPicker>
        </>
      )}

      <Separator />

      <SectionLabel>{t('configurator.seriesBy')}</SectionLabel>
      {activeSeriesDim ? (
        <div className="flex items-center gap-2 rounded-md border bg-background px-2.5 py-1.5 text-sm">
          <span className="flex-1 truncate">
            <DimensionLabel dimension={activeSeriesDim} />
          </span>
          <button
            onClick={() => setSeriesDim(null)}
            className="text-muted-foreground hover:text-foreground"
          >
            <X className="h-3.5 w-3.5" />
          </button>
        </div>
      ) : (
        <>
          <p className="text-xs text-muted-foreground">{t('configurator.seriesByHint')}</p>
          <DimensionPicker
            dimensions={dimensions}
            usedKeys={[xAxisKey, seriesBy]}
            onSelect={setSeriesDim}
          >
            <DimensionPickerTrigger>{t('dimensionPicker.chooseSeries')}</DimensionPickerTrigger>
          </DimensionPicker>
        </>
      )}

      <Separator />

      <SectionLabel>{t('configurator.yAxis')}</SectionLabel>
      {selectedMeasures.length > 0 ? (
        <div className="flex flex-col gap-1">
          {selectedMeasures.map((m, index) => {
            const color = CHART_COLORS[index % CHART_COLORS.length];
            return (
              <div
                key={m.key}
                className="flex items-center gap-2 rounded-md border bg-background px-2.5 py-1.5 text-sm"
              >
                <span
                  className="h-2.5 w-2.5 shrink-0 rounded-sm"
                  style={{ backgroundColor: color }}
                />
                {m.isFormula && (
                  // eslint-disable-next-line i18next/no-literal-string
                  <span className="shrink-0 font-mono text-xs text-primary">ƒ</span>
                )}
                <span className="min-w-0 flex-1 truncate">{m.label}</span>
                {m.hint && (
                  <SimpleTooltip
                    content={<span className="font-mono">{m.hint}</span>}
                    side="left"
                    variant="light"
                    contentClassName="max-w-xs text-xs"
                  >
                    <span className="cursor-help text-muted-foreground">
                      <Info className="h-3 w-3 shrink-0" />
                    </span>
                  </SimpleTooltip>
                )}
                <div className="flex shrink-0 gap-0.5">
                  <button
                    disabled={index === 0}
                    onClick={() => moveMeasure(m.key, -1)}
                    className="text-muted-foreground hover:text-foreground disabled:opacity-30"
                  >
                    <ChevronUp className="h-3.5 w-3.5" />
                  </button>
                  <button
                    disabled={index === selectedMeasures.length - 1}
                    onClick={() => moveMeasure(m.key, 1)}
                    className="text-muted-foreground hover:text-foreground disabled:opacity-30"
                  >
                    <ChevronDown className="h-3.5 w-3.5" />
                  </button>
                  <button
                    onClick={() => {
                      const next = columns.filter(k => k !== m.key);
                      setColumns(next);
                      save(next, groupBy);
                    }}
                    className="text-muted-foreground hover:text-foreground"
                  >
                    <X className="h-3.5 w-3.5" />
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      ) : (
        <p className="text-xs text-muted-foreground">{t('configurator.noMeasuresSelected')}</p>
      )}
      {regularMeasures.some(m => !columns.includes(m.key)) && (
        <div className="flex flex-col gap-1">
          <div className="relative">
            <Search className="pointer-events-none absolute left-2 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground" />
            <Input
              value={measureSearch}
              onChange={e => setMeasureSearch(e.target.value)}
              placeholder={t('configurator.searchMeasures')}
              className="h-7 pl-7 text-xs"
            />
          </div>
          <div className="flex max-h-48 flex-col overflow-y-auto">
            {filteredAvailableRegularMeasures.map(m => (
              <button
                key={m.key}
                type="button"
                onClick={() => {
                  const next = [...columns, m.key];
                  setColumns(next);
                  save(next, groupBy);
                }}
                className="flex items-center gap-2 rounded px-1.5 py-1.5 text-left text-sm text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
              >
                <Plus className="h-3.5 w-3.5 shrink-0" />
                <span className="truncate">{m.label}</span>
              </button>
            ))}
            {filteredAvailableRegularMeasures.length === 0 && measureSearch.trim() && (
              <p className="px-1.5 py-2 text-xs text-muted-foreground">
                {t('configurator.noSearchResults')}
              </p>
            )}
          </div>
        </div>
      )}

      {formulaMeasures.length > 0 && (
        <>
          <SectionLabel>{t('configurator.customColumns')}</SectionLabel>
          {availableFormulaMeasures.length > 0 ? (
            <div className="flex flex-col">
              {availableFormulaMeasures.map(m => (
                <div
                  key={m.key}
                  className="flex items-center rounded transition-colors hover:bg-muted"
                >
                  <button
                    type="button"
                    onClick={() => {
                      const next = [...columns, m.key];
                      setColumns(next);
                      save(next, groupBy);
                    }}
                    className="flex flex-1 items-center gap-2 px-1.5 py-1.5 text-left text-sm text-muted-foreground hover:text-foreground"
                  >
                    <Plus className="h-3.5 w-3.5 shrink-0" />
                    {/* eslint-disable-next-line i18next/no-literal-string */}
                    <span className="shrink-0 font-mono text-xs text-primary">ƒ</span>
                    <span className="truncate">{m.label}</span>
                  </button>
                  {m.hint && (
                    <SimpleTooltip
                      content={<span className="font-mono">{m.hint}</span>}
                      side="left"
                      variant="light"
                      contentClassName="max-w-xs text-xs"
                    >
                      <span className="cursor-help px-1.5 text-muted-foreground">
                        <Info className="h-3 w-3" />
                      </span>
                    </SimpleTooltip>
                  )}
                </div>
              ))}
            </div>
          ) : (
            <p className="text-xs text-muted-foreground">{t('configurator.allCustomAdded')}</p>
          )}
        </>
      )}

      <Separator />

      <div className="flex items-center justify-between gap-2">
        <span className="text-sm">{t('configurator.averageLine')}</span>
        <Switch
          checked={showAverageLine}
          onCheckedChange={checked => {
            setShowAverageLine(checked);
            saveConfig({ showAverageLine: checked });
          }}
        />
      </div>

      {showAverageLine && (
        <>
          <Input
            value={averageFormula}
            onChange={e => setAverageFormula(e.target.value)}
            onBlur={e => saveConfig({ averageFormula: e.target.value.trim() || null })}
            onKeyDown={e => {
              if (e.key === 'Enter') (e.target as HTMLInputElement).blur();
            }}
            placeholder="sum(profit) / sum(revenue) * 100"
            className="h-7 font-mono text-xs"
          />
          <p className="text-xs text-muted-foreground">{t('configurator.averageFormulaHint')}</p>
        </>
      )}

      <Separator />

      <SectionLabel>{t('configurator.referenceLine')}</SectionLabel>

      {targets.map((target, i) => (
        <div
          key={i}
          className="flex items-center gap-2 rounded-md border bg-background px-2.5 py-1.5 text-sm"
        >
          <span className="flex-1 truncate">{target.label}</span>
          <span className="shrink-0 tabular-nums text-muted-foreground">{target.value}</span>
          <button
            onClick={() => {
              const next = targets.filter((_, idx) => idx !== i);
              setTargets(next);
              saveConfig({ targets: next });
            }}
            className="text-muted-foreground hover:text-foreground"
          >
            <X className="h-3.5 w-3.5" />
          </button>
        </div>
      ))}

      <div className="flex gap-1.5">
        <Input
          value={newTargetLabel}
          onChange={e => setNewTargetLabel(e.target.value)}
          placeholder={t('configurator.referenceLineLabel')}
          className="h-7 min-w-0 flex-1 text-xs"
        />
        <Input
          value={newTargetValue}
          onChange={e => setNewTargetValue(e.target.value)}
          placeholder={t('configurator.referenceLineValue')}
          type="number"
          className="h-7 w-20 text-xs"
        />
        <Button
          size="sm"
          variant="outline"
          className="h-7 shrink-0 px-2 text-xs"
          disabled={!newTargetLabel.trim() || newTargetValue === ''}
          onClick={() => {
            const next = [
              ...targets,
              { label: newTargetLabel.trim(), value: Number(newTargetValue) },
            ];
            setTargets(next);
            saveConfig({ targets: next });
            setNewTargetLabel('');
            setNewTargetValue('');
          }}
        >
          {t('configurator.addReferenceLine')}
        </Button>
      </div>

      <Separator />

      <SectionLabel>{t('configurator.referenceArea')}</SectionLabel>

      {zones.map((zone, i) => (
        <div
          key={i}
          className="flex items-center gap-2 rounded-md border bg-background px-2.5 py-1.5 text-sm"
        >
          <span
            className="h-3 w-3 shrink-0 rounded-sm border"
            style={{ backgroundColor: zone.color }}
          />
          <span className="flex-1 truncate text-xs">
            {zone.label || t('configurator.referenceAreaNoLabel')}
          </span>
          <span className="shrink-0 text-xs tabular-nums text-muted-foreground">
            {zone.from ?? t('configurator.referenceAreaNegInfinity')}{' '}
            {t('configurator.referenceAreaRangeSeparator')}{' '}
            {zone.to ?? t('configurator.referenceAreaPosInfinity')}
          </span>
          <button
            onClick={() => {
              const next = zones.filter((_, idx) => idx !== i);
              setZones(next);
              saveConfig({ zones: next });
            }}
            className="text-muted-foreground hover:text-foreground"
          >
            <X className="h-3.5 w-3.5" />
          </button>
        </div>
      ))}

      <div className="flex gap-1.5">
        <Input
          value={newZoneFrom}
          onChange={e => setNewZoneFrom(e.target.value)}
          placeholder={t('configurator.referenceAreaFrom')}
          type="number"
          className="h-7 w-16 text-xs"
        />
        <Input
          value={newZoneTo}
          onChange={e => setNewZoneTo(e.target.value)}
          placeholder={t('configurator.referenceAreaTo')}
          type="number"
          className="h-7 w-16 text-xs"
        />
        <Input
          value={newZoneLabel}
          onChange={e => setNewZoneLabel(e.target.value)}
          placeholder={t('configurator.referenceAreaLabel')}
          className="h-7 min-w-0 flex-1 text-xs"
        />
        <input
          type="color"
          value={newZoneColor.slice(0, 7)}
          onChange={e => setNewZoneColor(e.target.value + '20')}
          className="h-7 w-7 shrink-0 cursor-pointer rounded border bg-background p-0.5"
          title={t('configurator.referenceAreaColor')}
        />
        <Button
          size="sm"
          variant="outline"
          className="h-7 shrink-0 px-2 text-xs"
          disabled={newZoneFrom === '' && newZoneTo === ''}
          onClick={() => {
            const next: ChartZone[] = [
              ...zones,
              {
                from: newZoneFrom !== '' ? Number(newZoneFrom) : undefined,
                to: newZoneTo !== '' ? Number(newZoneTo) : undefined,
                color: newZoneColor,
                label: newZoneLabel.trim() || undefined,
              },
            ];
            setZones(next);
            saveConfig({ zones: next });
            setNewZoneFrom('');
            setNewZoneTo('');
            setNewZoneLabel('');
            setNewZoneColor('#ef444420');
          }}
        >
          {t('configurator.addReferenceArea')}
        </Button>
      </div>
    </>
  );
};

interface PieChartConfigProps {
  element: ReportElement;
  reportId: string;
  measures: Array<{ key: string; label: string; type: string }>;
  dimensions: ReportDimensionResponse[];
  formulaEntries: FormulaLibraryEntry[];
}

const PieChartConfig = ({
  element,
  reportId,
  measures,
  dimensions,
  formulaEntries,
}: PieChartConfigProps) => {
  const { t } = useTranslation('authenticated.reports');
  const [columns, setColumns] = useState<string[]>(element.columns);
  const [groupBy, setGroupBy] = useState<string[]>(element.groupBy);
  const { mutate: updateElement } = useUpdateElement();

  const save = (c: string[], g: string[]) => {
    updateElement({ reportId, elementId: element.id, body: { columns: c, groupBy: g } });
  };

  const sliceKey = groupBy[0] ?? null;
  const sliceDim = sliceKey ? dimensions.find(d => d.key === sliceKey) : null;
  const valueKey = columns[0] ?? null;

  const setSlice = (key: string | null) => {
    const next = key ? [key] : [];
    setGroupBy(next);
    save(columns, next);
  };

  const setValue = (key: string | null) => {
    const next = key ? [key] : [];
    setColumns(next);
    save(next, groupBy);
  };

  const allMeasures = [
    ...measures,
    ...(formulaEntries ?? []).map(f => ({ key: f.key, label: f.label, type: f.measureType })),
  ];

  return (
    <>
      <SectionLabel>{t('configurator.sliceBy')}</SectionLabel>
      {sliceDim ? (
        <div className="flex items-center gap-2 rounded-md border bg-background px-2.5 py-1.5 text-sm">
          <span className="flex-1 truncate">
            <DimensionLabel dimension={sliceDim} />
          </span>
          <button
            onClick={() => setSlice(null)}
            className="text-muted-foreground hover:text-foreground"
          >
            <X className="h-3.5 w-3.5" />
          </button>
        </div>
      ) : (
        <>
          <p className="text-xs text-muted-foreground">{t('configurator.sliceByHint')}</p>
          <DimensionPicker dimensions={dimensions} usedKeys={[sliceKey]} onSelect={setSlice}>
            <DimensionPickerTrigger>{t('dimensionPicker.chooseSlices')}</DimensionPickerTrigger>
          </DimensionPicker>
        </>
      )}

      <Separator />

      <SectionLabel>{t('configurator.value')}</SectionLabel>
      {valueKey ? (
        <div className="flex items-center gap-2 rounded-md border bg-background px-2.5 py-1.5 text-sm">
          <span className="flex-1 truncate">
            {allMeasures.find(m => m.key === valueKey)?.label ?? valueKey}
          </span>
          <button
            onClick={() => setValue(null)}
            className="text-muted-foreground hover:text-foreground"
          >
            <X className="h-3.5 w-3.5" />
          </button>
        </div>
      ) : (
        <>
          <p className="text-xs text-muted-foreground">{t('configurator.valueHint')}</p>
          {allMeasures.length > 0 && (
            <div className="flex flex-wrap gap-1.5">
              {allMeasures.map(m => (
                <button
                  key={m.key}
                  onClick={() => setValue(m.key)}
                  className="flex items-center gap-1 rounded-full border px-2.5 py-0.5 text-xs text-muted-foreground transition-colors hover:border-foreground hover:text-foreground"
                >
                  <Plus className="h-2.5 w-2.5" />
                  {m.label}
                </button>
              ))}
            </div>
          )}
        </>
      )}
    </>
  );
};

interface KpiCardConfigProps {
  element: ReportElement;
  reportId: string;
  measures: Array<{ key: string; label: string; type: string }>;
  formulaEntries: FormulaLibraryEntry[];
}

const KpiCardConfig = ({ element, reportId, measures, formulaEntries }: KpiCardConfigProps) => {
  const { t } = useTranslation('authenticated.reports');
  const [columns, setColumns] = useState<string[]>(element.columns);
  const [measureSearch, setMeasureSearch] = useState('');
  const { mutate: updateElement } = useUpdateElement();

  const regularMeasures = measures.map(m => ({
    key: m.key,
    label: m.label,
    type: m.type,
    isFormula: false as const,
    hint: undefined as string | undefined,
  }));
  const formulaMeasures = (formulaEntries ?? []).map(f => ({
    key: f.key,
    label: f.label,
    type: f.measureType,
    isFormula: true as const,
    hint: f.formulaString,
  }));
  const allMeasures = [...regularMeasures, ...formulaMeasures];

  const selectedKey = columns[0] ?? null;
  const selectedMeasure = selectedKey ? allMeasures.find(m => m.key === selectedKey) : null;

  const setMeasure = (key: string | null) => {
    const next = key ? [key] : [];
    setColumns(next);
    updateElement({ reportId, elementId: element.id, body: { columns: next } });
  };

  const filteredRegular = regularMeasures
    .filter(m => m.key !== selectedKey)
    .filter(
      m => !measureSearch.trim() || m.label.toLowerCase().includes(measureSearch.toLowerCase())
    );
  const availableFormulas = formulaMeasures.filter(m => m.key !== selectedKey);

  return (
    <>
      <SectionLabel>{t('configurator.measure')}</SectionLabel>
      {selectedMeasure ? (
        <div className="flex items-center gap-2 rounded-md border bg-background px-2.5 py-1.5 text-sm">
          {selectedMeasure.isFormula && (
            // eslint-disable-next-line i18next/no-literal-string
            <span className="shrink-0 font-mono text-xs text-primary">ƒ</span>
          )}
          <span className="min-w-0 flex-1 truncate">{selectedMeasure.label}</span>
          {selectedMeasure.hint && (
            <SimpleTooltip
              content={<span className="font-mono">{selectedMeasure.hint}</span>}
              side="left"
              variant="light"
              contentClassName="max-w-xs text-xs"
            >
              <span className="cursor-help text-muted-foreground">
                <Info className="h-3 w-3 shrink-0" />
              </span>
            </SimpleTooltip>
          )}
          <button
            onClick={() => setMeasure(null)}
            className="text-muted-foreground hover:text-foreground"
          >
            <X className="h-3.5 w-3.5" />
          </button>
        </div>
      ) : (
        <p className="text-xs text-muted-foreground">{t('configurator.measureHint')}</p>
      )}

      {regularMeasures.some(m => m.key !== selectedKey) && (
        <div className="flex flex-col gap-1">
          <div className="relative">
            <Search className="pointer-events-none absolute left-2 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground" />
            <Input
              value={measureSearch}
              onChange={e => setMeasureSearch(e.target.value)}
              placeholder={t('configurator.searchMeasures')}
              className="h-7 pl-7 text-xs"
            />
          </div>
          <div className="flex max-h-48 flex-col overflow-y-auto">
            {filteredRegular.map(m => (
              <button
                key={m.key}
                type="button"
                onClick={() => setMeasure(m.key)}
                className="flex items-center gap-2 rounded px-1.5 py-1.5 text-left text-sm text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
              >
                <Plus className="h-3.5 w-3.5 shrink-0" />
                <span className="truncate">{m.label}</span>
              </button>
            ))}
            {filteredRegular.length === 0 && measureSearch.trim() && (
              <p className="px-1.5 py-2 text-xs text-muted-foreground">
                {t('configurator.noSearchResults')}
              </p>
            )}
          </div>
        </div>
      )}

      {formulaMeasures.length > 0 && (
        <>
          <SectionLabel>{t('configurator.customColumns')}</SectionLabel>
          {availableFormulas.length > 0 ? (
            <div className="flex flex-col">
              {availableFormulas.map(m => (
                <div
                  key={m.key}
                  className="flex items-center rounded transition-colors hover:bg-muted"
                >
                  <button
                    type="button"
                    onClick={() => setMeasure(m.key)}
                    className="flex flex-1 items-center gap-2 px-1.5 py-1.5 text-left text-sm text-muted-foreground hover:text-foreground"
                  >
                    <Plus className="h-3.5 w-3.5 shrink-0" />
                    {/* eslint-disable-next-line i18next/no-literal-string */}
                    <span className="shrink-0 font-mono text-xs text-primary">ƒ</span>
                    <span className="truncate">{m.label}</span>
                  </button>
                  {m.hint && (
                    <SimpleTooltip
                      content={<span className="font-mono">{m.hint}</span>}
                      side="left"
                      variant="light"
                      contentClassName="max-w-xs text-xs"
                    >
                      <span className="cursor-help px-1.5 text-muted-foreground">
                        <Info className="h-3 w-3" />
                      </span>
                    </SimpleTooltip>
                  )}
                </div>
              ))}
            </div>
          ) : (
            <p className="text-xs text-muted-foreground">{t('configurator.allCustomAdded')}</p>
          )}
        </>
      )}
    </>
  );
};

// ─── Element panel ────────────────────────────────────────────────────────────

interface ElementPanelProps {
  element: ReportElement;
  reportId: string;
  onBack: () => void;
  onNavigateToLibrary: () => void;
}

const ElementPanel = ({ element, reportId, onBack, onNavigateToLibrary }: ElementPanelProps) => {
  const { t } = useTranslation('authenticated.reports');
  const [title, setTitle] = useState<string>((element.config.title as string | undefined) ?? '');
  const [groupBy, setGroupBy] = useState<string[]>(element.groupBy);
  const [pivotDimension, setPivotDimension] = useState<string | null>(element.pivotDimension);
  const [newFormulaOpen, setNewFormulaOpen] = useState(false);
  const { mutate: updateElement } = useUpdateElement();

  const { data: columnsData } = useAvailableColumns(element.reportType);
  const { data: formulaEntries } = useFormulaEntries(element.reportType ?? undefined);

  const measures = columnsData?.measures ?? [];
  const dimensions = columnsData?.dimensions ?? [];
  const formulaList = formulaEntries ?? [];

  const availableColumns = [
    ...measures.map(m => ({ key: m.key, label: m.label })),
    ...dimensions.map(d => ({ key: d.key, label: d.label })),
    ...formulaList.map(f => ({ key: f.key, label: f.label })),
  ];

  const activePivotDim = pivotDimension ? dimensions.find(d => d.key === pivotDimension) : null;
  const usedDimensionKeys = [...groupBy, pivotDimension];

  const typeLabel = t(`element.${element.type}`);
  const isTextBlock = element.type === ReportElementType.TEXT_BLOCK;
  const isCartesian =
    element.type === ReportElementType.BAR_CHART ||
    element.type === ReportElementType.LINE_CHART ||
    element.type === ReportElementType.AREA_CHART;

  const saveTitle = (value: string) => {
    updateElement({
      reportId,
      elementId: element.id,
      body: { config: { ...element.config, title: value || null } },
    });
  };

  const saveWidth = (width: number) => {
    updateElement({
      reportId,
      elementId: element.id,
      body: { position: { ...element.position, width } },
    });
  };

  const saveReportType = (reportType: string) => {
    updateElement({
      reportId,
      elementId: element.id,
      body: { reportType, columns: [], groupBy: [], pivotDimension: null },
    });
    setGroupBy([]);
    setPivotDimension(null);
  };

  const saveGroupBy = (next: string[], pivot: string | null = pivotDimension) => {
    updateElement({
      reportId,
      elementId: element.id,
      body: { groupBy: next, pivotDimension: pivot },
    });
  };

  const addGroupBy = (key: string) => {
    const next = [...groupBy, key];
    setGroupBy(next);
    saveGroupBy(next);
  };

  const removeGroupBy = (key: string) => {
    const next = groupBy.filter(k => k !== key);
    setGroupBy(next);
    saveGroupBy(next);
  };

  const moveGroupBy = (index: number, dir: -1 | 1) => {
    const next = [...groupBy];
    const target = index + dir;
    if (target < 0 || target >= next.length) return;
    [next[index], next[target]] = [next[target], next[index]];
    setGroupBy(next);
    saveGroupBy(next);
  };

  const savePivot = (key: string | null) => {
    setPivotDimension(key);
    updateElement({ reportId, elementId: element.id, body: { pivotDimension: key } });
  };

  return (
    <div className="flex h-full w-72 shrink-0 flex-col overflow-hidden rounded-lg border bg-card">
      <PanelHeader backLabel={t('panel.report')} title={typeLabel} onBack={onBack} />

      <div className="flex-1 overflow-y-auto">
        {/* Title */}
        {!isTextBlock && (
          <div className="border-b px-3 py-2">
            <Input
              className="h-8 text-sm"
              value={title}
              onChange={e => setTitle(e.target.value)}
              onBlur={e => saveTitle(e.target.value)}
              onKeyDown={e => {
                if (e.key === 'Enter') (e.target as HTMLInputElement).blur();
              }}
              placeholder={t('configurator.titlePlaceholder')}
            />
          </div>
        )}

        {/* Width */}
        <WidthSelector currentWidth={element.position.width} onChange={saveWidth} />

        {/* Data source */}
        {!isTextBlock && (
          <>
            <Separator />
            <div className="flex flex-col gap-2 p-4">
              <SectionLabel>{t('configurator.dataSource')}</SectionLabel>
              <Select value={element.reportType ?? ''} onValueChange={saveReportType}>
                <SelectTrigger className="h-9 w-full">
                  <SelectValue placeholder={t('configurator.selectDataSource')} />
                </SelectTrigger>
                <SelectContent>
                  {REPORT_TYPES.map(rt => (
                    <SelectItem key={rt.value} value={rt.value}>
                      {t(rt.labelKey)}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </>
        )}

        {/* Group by */}
        {!isTextBlock && element.reportType && (
          <>
            <Separator />
            <div className="flex flex-col gap-2.5 p-4">
              <SectionLabel>{t('configurator.rows')}</SectionLabel>
              {groupBy.length > 0 && (
                <div className="flex flex-col gap-1">
                  {groupBy.map((key, index) => {
                    const dim = dimensions.find(d => d.key === key);
                    return (
                      <div
                        key={key}
                        className="flex items-center gap-2 rounded-md border bg-background px-2.5 py-1.5 text-sm"
                      >
                        <span className="w-3.5 shrink-0 text-xs text-muted-foreground">
                          {index + 1}
                        </span>
                        <span className="min-w-0 flex-1 truncate">
                          {dim ? <DimensionLabel dimension={dim} /> : key}
                        </span>
                        {dim?.key === DIMENSION_KEY_USER && (
                          <DimensionPicker
                            dimensions={dimensions}
                            usedKeys={usedDimensionKeys}
                            onSelect={addGroupBy}
                            scope={ReportDimensionEntityDTO.MEMBER}
                            align="end"
                          >
                            <button
                              type="button"
                              aria-label={t('dimensionPicker.addMemberField')}
                              title={t('dimensionPicker.addMemberField')}
                              className="text-muted-foreground hover:text-foreground data-[state=open]:text-foreground"
                            >
                              <ListPlus className="h-3.5 w-3.5" />
                            </button>
                          </DimensionPicker>
                        )}
                        <button
                          disabled={index === 0}
                          onClick={() => moveGroupBy(index, -1)}
                          className="text-muted-foreground hover:text-foreground disabled:opacity-30"
                        >
                          <ChevronUp className="h-3.5 w-3.5" />
                        </button>
                        <button
                          disabled={index === groupBy.length - 1}
                          onClick={() => moveGroupBy(index, 1)}
                          className="text-muted-foreground hover:text-foreground disabled:opacity-30"
                        >
                          <ChevronDown className="h-3.5 w-3.5" />
                        </button>
                        <button
                          onClick={() => removeGroupBy(key)}
                          className="text-muted-foreground hover:text-foreground"
                        >
                          <X className="h-3.5 w-3.5" />
                        </button>
                      </div>
                    );
                  })}
                </div>
              )}
              <DimensionPicker
                dimensions={dimensions}
                usedKeys={usedDimensionKeys}
                onSelect={addGroupBy}
              >
                <DimensionPickerTrigger>{t('dimensionPicker.addRow')}</DimensionPickerTrigger>
              </DimensionPicker>
            </div>

            <Separator />

            <div className="flex flex-col gap-2.5 p-4">
              <SectionLabel>{t('configurator.pivotColumn')}</SectionLabel>
              {activePivotDim ? (
                <div className="flex items-center gap-2 rounded-md border bg-background px-2.5 py-1.5 text-sm">
                  <span className="flex-1 truncate">
                    <DimensionLabel dimension={activePivotDim} />
                  </span>
                  <button
                    onClick={() => savePivot(null)}
                    className="text-muted-foreground hover:text-foreground"
                  >
                    <X className="h-3.5 w-3.5" />
                  </button>
                </div>
              ) : (
                <>
                  <p className="text-xs text-muted-foreground">
                    {t('configurator.pivotColumnHint')}
                  </p>
                  <DimensionPicker
                    dimensions={dimensions}
                    usedKeys={usedDimensionKeys}
                    onSelect={savePivot}
                  >
                    <DimensionPickerTrigger>
                      {t('dimensionPicker.addPivotColumn')}
                    </DimensionPickerTrigger>
                  </DimensionPicker>
                </>
              )}
            </div>
          </>
        )}

        {/* Type-specific config */}
        {element.reportType && (
          <>
            <Separator />
            <div className="flex flex-col gap-2.5 p-4">
              {element.type === ReportElementType.TABLE && (
                <TableColumns
                  element={element}
                  reportId={reportId}
                  measures={measures}
                  formulaEntries={formulaList}
                />
              )}

              {isCartesian && (
                <CartesianChartConfig
                  element={element}
                  reportId={reportId}
                  measures={measures}
                  dimensions={dimensions}
                  formulaEntries={formulaList}
                />
              )}

              {element.type === ReportElementType.PIE_CHART && (
                <PieChartConfig
                  element={element}
                  reportId={reportId}
                  measures={measures}
                  dimensions={dimensions}
                  formulaEntries={formulaList}
                />
              )}

              {element.type === ReportElementType.KPI_CARD && (
                <KpiCardConfig
                  element={element}
                  reportId={reportId}
                  measures={measures}
                  formulaEntries={formulaList}
                />
              )}
            </div>
          </>
        )}

        {/* Text block */}
        {isTextBlock && (
          <>
            <Separator />
            <div className="flex flex-col gap-2 p-4">
              <SectionLabel>{t('configurator.textContent')}</SectionLabel>
              <Textarea
                className="resize-none text-sm"
                rows={4}
                defaultValue={(element.config.content as string | undefined) ?? ''}
                onBlur={e => {
                  updateElement({
                    reportId,
                    elementId: element.id,
                    body: { config: { ...element.config, content: e.target.value } },
                  });
                }}
                placeholder={t('configurator.textPlaceholder')}
              />
            </div>
          </>
        )}
      </div>

      {/* Footer */}
      {!isTextBlock && element.reportType && (
        <div className="flex shrink-0 flex-col gap-2 border-t p-3">
          <Button
            size="sm"
            variant="outline"
            className="w-full"
            onClick={() => setNewFormulaOpen(true)}
          >
            <Plus className="mr-1 h-3.5 w-3.5" />
            {t('configurator.customColumn')}
          </Button>
          <Button size="sm" variant="outline" className="w-full" onClick={onNavigateToLibrary}>
            {t('configurator.browseLibrary')}
          </Button>
        </div>
      )}

      <FormulaDialog
        open={newFormulaOpen}
        onOpenChange={setNewFormulaOpen}
        availableColumns={availableColumns}
      />
    </div>
  );
};

// ─── Formula library panel ────────────────────────────────────────────────────

interface FormulaLibraryPanelProps {
  element: ReportElement;
  reportId: string;
  onBack: () => void;
}

const FormulaLibraryPanel = ({ element, reportId, onBack }: FormulaLibraryPanelProps) => {
  const { t } = useTranslation('authenticated.reports');
  const [tab, setTab] = useState<'mine' | 'org'>('mine');
  const [newFormulaOpen, setNewFormulaOpen] = useState(false);
  const [editFormula, setEditFormula] = useState<FormulaLibraryEntry | null>(null);

  const { data: formulaEntries } = useFormulaEntries(element.reportType ?? undefined);
  const { data: columnsData } = useAvailableColumns(element.reportType);
  const { mutate: updateElement } = useUpdateElement();
  const { mutate: deleteEntry } = useDeleteFormulaEntry();

  const entries = formulaEntries ?? [];
  const availableColumns = [
    ...(columnsData?.measures ?? []).map(m => ({ key: m.key, label: m.label })),
    ...(columnsData?.dimensions ?? []).map(d => ({ key: d.key, label: d.label })),
    ...entries.map(f => ({ key: f.key, label: f.label })),
  ];

  const typeLabel = t(`element.${element.type}`);
  const myEntries = entries.filter(e => e.scope === FormulaScope.USER);
  const orgEntries = entries.filter(e => e.scope === FormulaScope.ORG);
  const visibleEntries = tab === 'mine' ? myEntries : orgEntries;

  const insertFormula = (entry: FormulaLibraryEntry) => {
    if (!element.columns.includes(entry.key)) {
      updateElement({
        reportId,
        elementId: element.id,
        body: { columns: [...element.columns, entry.key] },
      });
    }
    onBack();
  };

  return (
    <div className="flex h-full w-72 shrink-0 flex-col overflow-hidden rounded-lg border bg-card">
      <PanelHeader backLabel={typeLabel} title={t('panel.formulaLibrary')} onBack={onBack} />

      {/* Tab bar */}
      <div className="flex shrink-0 border-b">
        {/* eslint-disable-next-line i18next/no-literal-string */}
        {(['mine', 'org'] as const).map(t2 => (
          <button
            key={t2}
            type="button"
            onClick={() => setTab(t2)}
            className={`flex-1 py-2 text-xs font-medium transition-colors ${
              tab === t2
                ? 'border-b-2 border-foreground text-foreground'
                : 'text-muted-foreground hover:text-foreground'
            }`}
          >
            {t2 === 'mine' ? t('formula.tabMine') : t('formula.tabOrg')}
          </button>
        ))}
      </div>

      <div className="flex-1 overflow-y-auto p-4">
        {visibleEntries.length === 0 ? (
          <div className="flex flex-col items-center gap-2 rounded-md border border-dashed p-6 text-center">
            {/* eslint-disable-next-line i18next/no-literal-string */}
            <span className="font-mono text-2xl text-muted-foreground">ƒ</span>
            <p className="text-xs font-medium">{t('formula.emptyTitle')}</p>
            <p className="text-xs text-muted-foreground">{t('formula.emptyLibraryHint')}</p>
          </div>
        ) : (
          <div className="flex flex-col gap-2">
            {visibleEntries.map(entry => (
              <div key={entry.id} className="rounded-md border bg-background p-3">
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0">
                    <div className="flex items-center gap-1.5">
                      {/* eslint-disable-next-line i18next/no-literal-string */}
                      <span className="shrink-0 font-mono text-xs text-primary">ƒ</span>
                      <p className="truncate text-sm font-medium">{entry.label}</p>
                    </div>
                    <p className="mt-0.5 truncate font-mono text-xs text-muted-foreground">
                      {entry.formulaString}
                    </p>
                  </div>
                  <Button
                    size="sm"
                    variant="outline"
                    className="h-6 shrink-0 px-2 text-xs"
                    onClick={() => insertFormula(entry)}
                  >
                    {t('formula.insert')}
                  </Button>
                </div>
                <div className="mt-2 flex items-center gap-1.5">
                  <span className="rounded bg-muted px-1.5 py-0.5 text-xs text-muted-foreground">
                    {MEASURE_TYPE_LABELS[entry.measureType] ?? entry.measureType}
                  </span>
                  {entry.scope === FormulaScope.ORG && (
                    <span className="rounded bg-primary/10 px-1.5 py-0.5 text-xs text-primary">
                      {t('formula.scopeOrg')}
                    </span>
                  )}
                  <button
                    type="button"
                    onClick={() => setEditFormula(entry)}
                    className="ml-auto text-xs text-muted-foreground transition-colors hover:text-foreground"
                  >
                    {t('common.edit')}
                  </button>
                  <button
                    type="button"
                    onClick={() => deleteEntry(entry.id)}
                    className="text-xs text-muted-foreground transition-colors hover:text-destructive"
                  >
                    {t('common.delete')}
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      <div className="shrink-0 border-t p-3">
        <Button
          size="sm"
          variant="outline"
          className="w-full"
          onClick={() => setNewFormulaOpen(true)}
        >
          <Plus className="mr-1 h-3.5 w-3.5" />
          {t('formula.new')}
        </Button>
      </div>

      <FormulaDialog
        open={newFormulaOpen}
        onOpenChange={setNewFormulaOpen}
        availableColumns={availableColumns}
      />
      {editFormula && (
        <FormulaDialog
          open
          onOpenChange={open => {
            if (!open) setEditFormula(null);
          }}
          availableColumns={availableColumns}
          entry={editFormula}
        />
      )}
    </div>
  );
};

// ─── Main export ──────────────────────────────────────────────────────────────

interface ReportRightPanelProps {
  report: Report;
  editedName: string;
  onNameChange: (name: string) => void;
  onDragStart: (item: DroppingItem) => void;
  onDragEnd: () => void;
  dateFilter: DateFilter;
  isEditing: boolean;
  selectedElementId: string | null;
  onDeselect: () => void;
}

export const ReportRightPanel = ({
  report,
  editedName,
  onNameChange,
  onDragStart,
  onDragEnd,
  dateFilter: _dateFilter,
  isEditing: _isEditing,
  selectedElementId,
  onDeselect,
}: ReportRightPanelProps) => {
  const [stack, setStack] = useState<PanelFrame[]>([]);

  useEffect(() => {
    if (selectedElementId) {
      setStack([{ kind: 'element', elementId: selectedElementId }]);
    } else {
      setStack(prev => (prev.length > 0 && prev[0].kind === 'element' ? [] : prev));
    }
  }, [selectedElementId]);

  const push = (frame: PanelFrame) => setStack(prev => [...prev, frame]);
  const pop = () => setStack(prev => prev.slice(0, -1));

  const top = stack[stack.length - 1];

  if (top?.kind === 'formula-library') {
    const element = report.elements.find(e => e.id === top.elementId);
    if (element) {
      return <FormulaLibraryPanel element={element} reportId={report.id} onBack={pop} />;
    }
  }

  if (top?.kind === 'element') {
    const element = report.elements.find(e => e.id === top.elementId);
    if (element) {
      return (
        <ElementPanel
          element={element}
          reportId={report.id}
          onBack={() => {
            pop();
            onDeselect();
          }}
          onNavigateToLibrary={() => push({ kind: 'formula-library', elementId: top.elementId })}
        />
      );
    }
  }

  return (
    <RootPanel
      report={report}
      editedName={editedName}
      onNameChange={onNameChange}
      onDragStart={onDragStart}
      onDragEnd={onDragEnd}
      onNavigateToElement={elementId => setStack([{ kind: 'element', elementId }])}
    />
  );
};
